import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ParsedUrlQuery } from 'querystring';

dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: '%ds',
    m: '1m',
    mm: '%dm',
    h: '1hr',
    hh: '%dhr',
    d: '1d',
    dd: '%dd',
    M: '1mo',
    MM: '%dmo',
    y: '1yr',
    yy: '%dyr',
  },
});

export { dayjs };

export const today = dayjs(new Date());

// ISO Date but without the time part, format is YYYY-MM-DD
export type DateString = string | null;

export type DateRange = [DateString, DateString];

export type DateRangePickerProps = { value: DateRange; onChange: (period: DateRange) => void };

export type DateRangeFormData = {
  range: DateRange;
};

const MONTH_DAY_YEAR_FORMAT = 'MMM D, YYYY'; // Define the expected format
const YEAR_MONTH_DAY_FORMAT = 'YYYY-MM-DD'; // Define the expected format

export const sixMonthsAgo = dayjs().subtract(6, 'month').toDate();

export const isBeforeSixMonths = (date: Date) => {
  const todayDate = new Date();
  const sixMonthsBefore = new Date(todayDate.getFullYear(), todayDate.getMonth() - 6, todayDate.getDate());
  const selectedDate = new Date(date);
  return selectedDate < sixMonthsBefore;
};
export const isAfterToday = (date: Date) => dayjs(date).isAfter(dayjs());

export function toDateString(range: [Dayjs, Dayjs] | DateRange) {
  return range.map((date) => {
    const dayjsDate = dayjs(date);
    return dayjsDate.isValid() ? dayjsDate.format('YYYY-MM-DD') : null;
  }) as DateRange;
}

export function fromDateString(range: DateRange) {
  return range.map((date) => (date ? new Date(date) : null));
}

export function backendCompatibleTimestamp(dateString: DateString) {
  let formattedDate = '';

  try {
    if (dateString) {
      const date = dayjs(new Date(dateString));
      formattedDate = `${date.format('YYYY-MM-DD')}T00:00:00Z`;
    }
  } catch {
    console.error('Error while formatting date:', dateString);
  }

  return formattedDate;
}

export function timeStampWithEndOfDay(dateString: DateString) {
  const date = dayjs(dateString).endOf('day');
  return date.toISOString();
}

export function backendCompatibleDateRange(dateRange: DateRange) {
  return dateRange.map(backendCompatibleTimestamp) as DateRange;
}

export function isEqualDateRange(range1: DateRange, range2: DateRange) {
  // Converts DateRange into an array of numeric timestamps, ignoring the timezone offset
  const convertToTimestamp = (dateRange: DateRange) =>
    dateRange
      .map(backendCompatibleTimestamp)
      .filter(Boolean)
      .map((date) => new Date(date).getTime());

  const nonEmptyDates1 = convertToTimestamp(range1);
  const nonEmptyDates2 = convertToTimestamp(range2);

  return nonEmptyDates1.length === nonEmptyDates2.length && nonEmptyDates1.every((date, index) => date === nonEmptyDates2[index]);
}

export function createDefaultSelectedDateRange(routerQuery: ParsedUrlQuery): DateRange {
  const queryFromDate = (routerQuery.fromDate as string) ?? null;
  const queryToDate = (routerQuery.toDate as string) ?? null;

  if (queryFromDate && queryToDate) {
    return [queryFromDate, queryToDate];
  }

  return DEFAULT_SELECTED_DATE_RANGE.range;
}

export const formatPeriod = (dateString: DateString) => dayjs(dateString).format('MMM D, YYYY');

export const FIXED_DATE_RANGES: { title: string; range: DateRange }[] = [
  {
    title: 'Today',
    range: toDateString([today, today]),
  },
  {
    title: 'Yesterday',
    range: toDateString([today.subtract(1, 'days'), today.subtract(1, 'days')]),
  },
  {
    title: 'Last 7 days',
    range: toDateString([today.subtract(7, 'days'), today.subtract(1, 'days')]),
  },
  {
    title: 'This week',
    range: toDateString([today.startOf('isoWeek'), today.endOf('isoWeek').isAfter(today) ? today : today.endOf('isoWeek')]),
  },
  {
    title: 'Last week',
    range: toDateString([today.subtract(1, 'weeks').startOf('isoWeek'), today.subtract(1, 'weeks').endOf('isoWeek')]),
  },
  {
    title: 'This month',
    range: toDateString([today.startOf('month'), today.endOf('month').isAfter(today) ? today : today.endOf('month')]),
  },
  {
    title: 'Last month',
    range: toDateString([today.subtract(1, 'months').startOf('month'), today.subtract(1, 'months').endOf('month')]),
  },
  {
    title: 'This quarter',
    range: toDateString([today.startOf('quarter'), today.endOf('quarter').isAfter(today) ? today : today.endOf('quarter')]),
  },
  {
    title: 'Last quarter',
    range: toDateString([today.subtract(1, 'quarters').startOf('quarter'), today.subtract(1, 'quarters').endOf('quarter')]),
  },
  {
    title: 'Last 6 months',
    range: toDateString([dayjs(new Date(new Date().setMonth(today.get('month') - 6))), today]),
  },
  // {
  //   title: 'This year',
  //   range: toISOString([today.startOf('year'), today]),
  // },
  // {
  //   title: 'Last year',
  //   range: toISOString([today.subtract(1, 'years').startOf('year'), today.subtract(1, 'years').endOf('year')]),
  // },
  // {
  //   title: 'All time',
  //   range: toISOString([today.startOf('year').subtract(1, 'years'), today]),
  // },
];

export const isSameDay = (date1: Date, date2: Date) => {
  return dayjs(date1).isSame(date2, 'day');
};

const isISO8601 = (dateString: string) => {
  const date = new Date(dateString);
  return !Number.isNaN(date.getTime()) && dateString === date.toISOString();
};

export const isValidDate = (date: DateString) => {
  return dayjs(date, MONTH_DAY_YEAR_FORMAT, true).isValid() || dayjs(date, YEAR_MONTH_DAY_FORMAT, true).isValid() || isISO8601(date as string);
};

export const DEFAULT_SELECTED_DATE_RANGE = FIXED_DATE_RANGES[2];
